import { motion } from 'framer-motion';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Flame, Target, TrendingUp, Trophy, Rocket, Bell, Star, Pin } from 'lucide-react';
import { api } from '@/lib/api';

// V2 Hero — sits at the very top of the Dashboard. Pure presentational.
export default function HeroSection({ journey, displayName, onBellClick, unreadCount = 0, onPrimaryChanged }) {
  const navigate = useNavigate();
  // iter-82: long-press a goal card → pin it as the main goal.
  const longPressTimer = useRef(null);
  const longPressed = useRef(false);
  const [pinningId, setPinningId] = useState(null);

  const pinAsMain = async (goalId, title) => {
    setPinningId(goalId);
    try {
      await api.post('/users/me/primary-goal', { goal_id: goalId });
      toast.success(`"${(title || 'Goal').slice(0, 40)}" is now your main goal`);
      onPrimaryChanged?.();
    } catch {
      toast.error('Could not pin as main');
    } finally {
      setPinningId(null);
    }
  };

  const startLongPress = (goalId, title, isPrimary) => {
    if (isPrimary) return; // already pinned
    longPressed.current = false;
    longPressTimer.current = setTimeout(() => {
      longPressed.current = true;
      pinAsMain(goalId, title);
      // Light haptic on supporting browsers.
      try { navigator.vibrate?.(30); } catch {}
    }, 550);
  };
  const cancelLongPress = () => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
    longPressTimer.current = null;
  };
  const handleClick = (goalId) => {
    if (longPressed.current) {
      longPressed.current = false; // swallow the click that follows the long-press
      return;
    }
    navigate(`/goals/${goalId}`);
  };

  if (!journey) return null;
  const { rank, hero } = journey;
  // iter-81: support multiple active goals as a horizontal carousel.
  // Backend now returns `hero.active_goals` (up to 3, primary first); fall
  // back to single main_goal for older payloads.
  const goals = (hero?.active_goals && hero.active_goals.length > 0)
    ? hero.active_goals
    : (hero?.main_goal ? [{ ...hero.main_goal, is_primary: true }] : []);
  const rankColor = rank?.current?.color || '#1CB0F6';

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="rounded-3xl p-5 text-white shadow-xl relative overflow-hidden"
      style={{
        background: `linear-gradient(135deg, ${rankColor} 0%, #4F46E5 100%)`,
      }}
      data-testid="home-hero"
    >
      {/* Notifications bell — top-right (Coach is now a global floating button) */}
      <div className="absolute top-3 right-3 z-20 flex items-center gap-2">
        <button
          onClick={onBellClick}
          aria-label="Notifications"
          className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition relative"
          data-testid="hero-notif-bell"
        >
          <Bell size={20} strokeWidth={3} />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 bg-[#FF4B4B] text-[10px] font-extrabold rounded-full w-5 h-5 flex items-center justify-center pointer-events-none">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </div>

      {/* Greeting + Rank chip */}
      <p className="text-xs uppercase font-extrabold opacity-80 tracking-wider">
        Good day, {displayName || 'there'}
      </p>
      <div className="flex items-center gap-2 mt-1">
        <span className="text-[11px] font-extrabold bg-white/20 px-2 py-1 rounded-full inline-flex items-center gap-1">
          <span>{rank?.current?.emoji}</span> {rank?.current?.name}
        </span>
        {rank?.next && (
          <span className="text-[11px] font-extrabold opacity-80">
            → {rank.next.name} in {rank.xp_to_next} XP
          </span>
        )}
      </div>

      {/* Streak + Missions row */}
      <div className="flex items-center gap-4 mt-4">
        <div className="flex items-center gap-1.5" data-testid="hero-streak">
          <Flame size={22} fill="#FFC800" color="#FFC800" strokeWidth={2.5} />
          <span className="text-2xl font-extrabold">{hero?.streak ?? 0}</span>
          <span className="text-[11px] font-extrabold opacity-80 uppercase ml-0.5">day streak</span>
        </div>
        <div className="h-6 w-px bg-white/20" />
        <div className="flex items-center gap-1.5" data-testid="hero-missions-waiting">
          <Target size={18} strokeWidth={3} />
          <span className="text-base font-extrabold">{hero?.missions_waiting ?? 0}</span>
          <span className="text-[11px] font-extrabold opacity-80 uppercase">missions waiting</span>
        </div>
      </div>

      {/* iter-81: Goals horizontal carousel — primary first, scroll-snap, tap to open detail */}
      {goals.length > 0 ? (
        <div
          className="mt-5 -mx-5 px-5 overflow-x-auto scroll-smooth snap-x snap-mandatory gm-hide-scrollbar"
          data-testid="hero-goals-carousel"
        >
          <div className="flex gap-3" style={{ paddingRight: goals.length > 1 ? 8 : 0 }}>
            {goals.map((g, idx) => (
              <button
                key={g.id}
                onClick={() => handleClick(g.id)}
                onMouseDown={() => startLongPress(g.id, g.title, g.is_primary)}
                onMouseUp={cancelLongPress}
                onMouseLeave={cancelLongPress}
                onTouchStart={() => startLongPress(g.id, g.title, g.is_primary)}
                onTouchEnd={cancelLongPress}
                onTouchCancel={cancelLongPress}
                className={`snap-start text-left bg-white/10 hover:bg-white/15 active:bg-white/20 rounded-2xl p-3 transition-colors flex-shrink-0 relative ${
                  goals.length === 1 ? 'w-full' : 'w-[88%] sm:w-[90%]'
                } ${pinningId === g.id ? 'ring-2 ring-[#FFC800]' : ''}`}
                data-testid={`hero-goal-card-${idx}`}
              >
                <div className="flex items-center gap-1.5 mb-0.5">
                  <p className="text-[10px] uppercase font-extrabold opacity-70 tracking-wider">
                    {g.is_primary ? 'Main goal' : 'Goal'}
                  </p>
                  {g.is_primary && <Star size={10} strokeWidth={3} fill="#FFC800" color="#FFC800" />}
                  {!g.is_primary && (
                    <span className="text-[9px] uppercase font-extrabold opacity-50 tracking-wider ml-auto inline-flex items-center gap-0.5">
                      <Pin size={9} strokeWidth={3} /> hold to pin
                    </span>
                  )}
                </div>
                <p className="text-lg font-extrabold leading-tight mt-0.5 truncate">{g.title}</p>

                <div className="mt-3 flex items-center gap-2 text-xs font-extrabold">
                  <TrendingUp size={14} strokeWidth={3} />
                  <span>Progress</span>
                  <span className="ml-auto" data-testid={idx === 0 ? 'hero-progress-pct' : undefined}>
                    {g.percent}%
                  </span>
                </div>
                <div className="mt-1 h-2 bg-white/15 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full rounded-full bg-[#FFC800]"
                    initial={{ width: 0 }}
                    animate={{ width: `${g.percent}%` }}
                    transition={{ duration: 0.6, ease: 'easeOut' }}
                  />
                </div>

                <div className="mt-3 flex items-start gap-2">
                  <Rocket size={14} strokeWidth={3} className="opacity-90 mt-0.5 flex-shrink-0" />
                  <p
                    className="text-xs font-extrabold opacity-90 leading-snug line-clamp-2"
                    data-testid={idx === 0 ? 'hero-next-milestone' : undefined}
                  >
                    {g.next_milestone}
                  </p>
                </div>
                <p className="text-[10px] font-extrabold opacity-70 mt-1">
                  Day {g.days_elapsed} of {g.days_total} · {g.days_remaining} days left
                </p>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="mt-5 bg-white/10 rounded-2xl p-3 text-center">
          <Trophy size={28} strokeWidth={2.5} className="mx-auto mb-1 opacity-80" />
          <p className="text-sm font-extrabold">Set your first goal to start tracking progress.</p>
        </div>
      )}

      {/* Pagination dots — only when multiple goals */}
      {goals.length > 1 && (
        <div className="flex items-center justify-center gap-1.5 mt-2.5" data-testid="hero-goals-dots">
          {goals.map((g, i) => (
            <span
              key={g.id}
              className={`h-1.5 rounded-full transition-all ${i === 0 ? 'w-4 bg-white' : 'w-1.5 bg-white/40'}`}
              aria-hidden
            />
          ))}
        </div>
      )}
    </motion.div>
  );
}
